<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="本所案件工作台（Matter）。所内开案与付后升格案均在此；原「本所案件」只读列表可逐步收敛到此页。"
      class="mb-12px"
    />
    <el-form :inline="true" :model="query" class="mb-12px" @submit.prevent>
      <el-form-item label="标题">
        <el-input v-model="query.title" clearable class="!w-160px" />
      </el-form-item>
      <el-form-item label="状态">
        <el-input v-model="query.practiceStatus" clearable placeholder="如 INTAKE" class="!w-120px" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button @click="resetQuery">重置</el-button>
        <el-button type="success" @click="openCreate">所内开案</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="80" />
      <el-table-column prop="firmCaseNo" label="所内案号" min-width="140" />
      <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
      <el-table-column prop="caseType" label="案由" width="100" />
      <el-table-column prop="customerName" label="客户" width="110" show-overflow-tooltip />
      <el-table-column prop="leadLawyerName" label="主办" width="100" />
      <el-table-column prop="practiceStatus" label="办案态" width="100" />
      <el-table-column label="来源" width="100">
        <template #default="{ row }">{{ matterSourceLabel(row.source) }}</template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="170" />
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">详情</el-button>
          <el-dropdown v-if="row.nextStatuses?.length" trigger="click" @command="(to) => doTransit(row, to)">
            <el-button link type="success">流转</el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item v-for="s in row.nextStatuses" :key="s" :command="s">{{ s }}</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="getList"
    />

    <Dialog v-model="createVisible" title="所内开案" width="520px">
      <el-form ref="createFormRef" :model="createForm" :rules="createRules" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="createForm.title" maxlength="128" />
        </el-form-item>
        <el-form-item label="案由" prop="caseType">
          <el-input v-model="createForm.caseType" placeholder="如 MARRIAGE / CONTRACT" />
        </el-form-item>
        <el-form-item label="客户" prop="customerId">
          <el-select
            v-model="createForm.customerId"
            filterable
            remote
            clearable
            :remote-method="searchCustomers"
            :loading="customerLoading"
            class="!w-100%"
            placeholder="输入姓名或手机号搜索"
            no-data-text="请输入姓名或手机号搜索"
          >
            <el-option
              v-for="c in customerOptions"
              :key="c.id"
              :label="`${c.name}（${c.mobile || c.customerNo || c.id}）`"
              :value="c.id!"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="主办律师" prop="leadLawyerId">
          <el-select v-model="createForm.leadLawyerId" filterable class="!w-100%" placeholder="本所可接单律师">
            <el-option
              v-for="l in lawyerOptions"
              :key="l.id"
              :label="`${l.name || '律师'}（#${l.id}）`"
              :value="l.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="摘要">
          <el-input v-model="createForm.description" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="submitCreate">开案</el-button>
      </template>
    </Dialog>

    <el-drawer v-model="detailVisible" title="案件详情" size="420px">
      <el-descriptions v-if="detail" :column="1" border>
        <el-descriptions-item label="所内案号">{{ detail.firmCaseNo || '—' }}</el-descriptions-item>
        <el-descriptions-item label="标题">{{ detail.title }}</el-descriptions-item>
        <el-descriptions-item label="案由">{{ detail.caseType }}</el-descriptions-item>
        <el-descriptions-item label="客户">{{ detail.customerName || detail.customerId }}</el-descriptions-item>
        <el-descriptions-item label="主办">{{ detail.leadLawyerName || detail.leadLawyerId }}</el-descriptions-item>
        <el-descriptions-item label="办案态">{{ detail.practiceStatus || '—' }}</el-descriptions-item>
        <el-descriptions-item label="来源">{{ matterSourceLabel(detail.source) }}</el-descriptions-item>
        <el-descriptions-item label="创建">{{ detail.createTime || '—' }}</el-descriptions-item>
      </el-descriptions>
    </el-drawer>
  </ContentWrap>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import type { FormInstance, FormRules } from 'element-plus'
import { FirmPracticeApi, type PracticeCustomerVO, type PracticeMatterVO } from '@/api/firm/practice'
import { FirmPortalApi, type FirmLawyerVO } from '@/api/firm/portal'
import { matterSourceLabel } from '@/api/lawyer/firmMatter'

defineOptions({ name: 'FirmPracticeMatter' })

const message = useMessage()
const loading = ref(false)
const list = ref<PracticeMatterVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  title: undefined as string | undefined,
  practiceStatus: undefined as string | undefined
})

const customerOptions = ref<PracticeCustomerVO[]>([])
const lawyerOptions = ref<FirmLawyerVO[]>([])
const customerLoading = ref(false)

const createVisible = ref(false)
const creating = ref(false)
const createFormRef = ref<FormInstance>()
const createForm = reactive({
  title: '',
  caseType: '',
  customerId: undefined as number | undefined,
  leadLawyerId: undefined as number | undefined,
  description: ''
})
const createRules: FormRules = {
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  caseType: [{ required: true, message: '请输入案由', trigger: 'blur' }],
  customerId: [{ required: true, message: '请选择客户', trigger: 'change' }],
  leadLawyerId: [{ required: true, message: '请选择主办律师', trigger: 'change' }]
}

const detailVisible = ref(false)
const detail = ref<PracticeMatterVO | null>(null)

const getList = async () => {
  loading.value = true
  try {
    const data = await FirmPracticeApi.matterPage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  query.pageNo = 1
  getList()
}

const resetQuery = () => {
  query.title = undefined
  query.practiceStatus = undefined
  handleQuery()
}

const searchCustomers = useDebounceFn(async (keyword?: string) => {
  const kw = String(keyword || '').trim()
  if (!kw) {
    const id = createForm.customerId
    customerOptions.value = id
      ? customerOptions.value.filter((c) => c.id === id)
      : []
    return
  }
  customerLoading.value = true
  try {
    const data = await FirmPracticeApi.customerPage({
      pageNo: 1,
      pageSize: 20,
      keyword: kw
    })
    customerOptions.value = data?.list || []
  } catch {
    customerOptions.value = []
  } finally {
    customerLoading.value = false
  }
}, 300)

const openCreate = async () => {
  createForm.title = ''
  createForm.caseType = ''
  createForm.customerId = undefined
  createForm.leadLawyerId = undefined
  createForm.description = ''
  customerOptions.value = []
  createVisible.value = true
  lawyerOptions.value = (await FirmPortalApi.lawyerAssignableList()) || []
}

const submitCreate = async () => {
  await createFormRef.value?.validate()
  if (!createForm.customerId || !createForm.leadLawyerId) return
  creating.value = true
  try {
    const id = await FirmPracticeApi.matterCreate({
      title: createForm.title.trim(),
      caseType: createForm.caseType.trim(),
      customerId: createForm.customerId,
      leadLawyerId: createForm.leadLawyerId,
      description: createForm.description || undefined
    })
    message.success(`已开案 #${id}`)
    createVisible.value = false
    await getList()
  } finally {
    creating.value = false
  }
}

const openDetail = async (row: PracticeMatterVO) => {
  detailVisible.value = true
  detail.value = row
  if (row.id) {
    detail.value = (await FirmPracticeApi.matterGet(row.id)) || row
  }
}

const doTransit = async (row: PracticeMatterVO, toStatus: string) => {
  if (!row.id) return
  try {
    await message.confirm(`确认将案件流转到「${toStatus}」？`)
  } catch {
    return
  }
  await FirmPracticeApi.matterTransition({ id: row.id, toStatus })
  message.success('已流转')
  await getList()
}

onMounted(getList)
</script>
